import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationOverlap,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  STATUS_LABELS,
  investigationPath,
  resolutionReasonLabel,
} from "./investigationUtils";

/** Rows shown before the list collapses behind a "Show all" toggle. */
export const OVERLAP_PREVIEW_ROWS = 3;

/**
 * Plain-language description of how an existing investigation's rule relates to the draft.
 * `relation` is computed as rules_overlap(draft, existing), so `rule_subset` means the DRAFT
 * is the narrower one — i.e. the existing rule is broader.
 */
/**
 * Cancelled investigations stay quiet: they are shown on the Cancelled page and on the case
 * itself, never in creation flows. The API already leaves them out; this also covers previews
 * computed before a case was cancelled. Merged and duplicate cases are hidden the same way:
 * their items belong to another case, so listing them as "resolved" prior art is misleading.
 */
const HIDDEN_REASONS = new Set(["MERGED", "DUPLICATE"]);

export const withoutCancelled = (
  overlaps?: InvestigationOverlap[] | null
): InvestigationOverlap[] =>
  (overlaps || []).filter(
    (o) =>
      o.status !== "WITHDRAWN" && !HIDDEN_REASONS.has(o.resolutionReason ?? "")
  );

export function relationLabel(relation?: string | null): string {
  switch (relation) {
    case "rule_subset":
      return "broader rule";
    case "rule_superset":
      return "narrower rule";
    case "rule_overlap":
      return "overlapping rule";
    case "item_overlap":
      return "different rule, owns some of these items";
    default:
      return "";
  }
}

export function sharedItemsLabel(
  overlappingCount?: number | null,
  totalItems?: number | null
): string {
  const n = overlappingCount ?? 0;
  if (n === 0) return "no shared items yet";
  const shared = n.toLocaleString();
  return totalItems
    ? `${shared} of ${totalItems.toLocaleString()} items`
    : `${shared} shared items`;
}

const OverlapTitleLink: React.FC<{
  stage: Stage;
  overlap: InvestigationOverlap;
}> = ({ stage, overlap }) => {
  const title = overlap.title || overlap.investigationId || "Investigation";
  return (
    <Link
      external
      href={investigationPath(overlap.investigationId || "", stage)}
      ariaLabel={`Open investigation ${title} in a new tab`}
    >
      {title}
    </Link>
  );
};

/** Title on one line, a single muted detail line underneath — no badges. */
export const OverlapRow: React.FC<{
  stage: Stage;
  overlap: InvestigationOverlap;
  details: string[];
}> = ({ stage, overlap, details }) => (
  <div>
    <OverlapTitleLink stage={stage} overlap={overlap} />
    <Box color="text-body-secondary" fontSize="body-s">
      {details.filter(Boolean).join(" · ")}
    </Box>
  </div>
);

const CollapsibleRows: React.FC<{
  rows: React.ReactNode[];
  noun: string;
}> = ({ rows, noun }) => {
  const [expanded, setExpanded] = useState(false);
  const hidden = rows.length - OVERLAP_PREVIEW_ROWS;
  const visible = expanded ? rows : rows.slice(0, OVERLAP_PREVIEW_ROWS);
  return (
    <SpaceBetween size="xs">
      {visible}
      {hidden > 0 && (
        <Button
          variant="inline-link"
          onClick={() => setExpanded((e) => !e)}
          ariaExpanded={expanded}
        >
          {expanded
            ? "Show fewer"
            : `Show ${hidden} more ${noun}${hidden === 1 ? "" : "s"}`}
        </Button>
      )}
    </SpaceBetween>
  );
};

/**
 * What the overlap dialog will actually offer on create, in one sentence. Must mirror
 * InvestigationOverlapDialog: a narrower child only against a BROADER existing rule
 * (`rule_subset`), the remainder only when some items are unowned.
 */
export function overlapChoicesSentence(
  overlaps: InvestigationOverlap[],
  unassignedCount?: number | null
): string {
  const n = overlaps.length;
  const contribute =
    n === 1 ? "contribute to it" : "contribute to one of these";
  const options: string[] = [];
  if (unassignedCount == null || unassignedCount > 0)
    options.push(
      typeof unassignedCount === "number"
        ? `create for the ${unassignedCount.toLocaleString()} unowned item${
            unassignedCount === 1 ? "" : "s"
          } no other case covers`
        : "create for the unowned remainder"
    );
  if (overlaps.some((o) => o.relation === "rule_subset"))
    options.push("split a narrower case out of the broader investigation");
  if (options.length === 0)
    return `Every matching item is already owned or covered by another case, so creating isn't possible – ${contribute} instead.`;
  options.push(contribute);
  const last = options.pop() ?? contribute;
  return `When you create, you can ${options.join(", ")} or ${last}.`;
}

/** Share of the draft one existing case must cover before the page recommends working there. */
export const MOSTLY_COVERED_SHARE = 0.8;

/**
 * The one active case that covers at least MOSTLY_COVERED_SHARE of the draft (items it owns plus
 * unowned ones it wins under narrowest-then-oldest), or undefined. A new case would get little
 * beyond what that case already has, so the page steers towards it instead of a near-duplicate.
 */
export function mostlyCoveredBy(
  overlaps: InvestigationOverlap[],
  totalItems?: number | null
): InvestigationOverlap | undefined {
  if (!totalItems) return undefined;
  const top = [...withoutCancelled(overlaps)].sort(
    (a, b) => (b.overlappingCount ?? 0) - (a.overlappingCount ?? 0)
  )[0];
  return top && (top.overlappingCount ?? 0) / totalItems >= MOSTLY_COVERED_SHARE
    ? top
    : undefined;
}

interface ActiveOverlapsAlertProps {
  stage: Stage;
  overlaps: InvestigationOverlap[];
  /** Size of the previewed population, for "x of y items". */
  totalItems?: number | null;
  /**
   * Items a new case would get: neither owned nor won (narrowest, then oldest) by an active
   * investigation. Decides whether "remainder" is offered.
   */
  unassignedCount?: number | null;
  /** Viewer alias: "Add to <case>" is offered only on a mostly-covering case they own. */
  currentUser?: string | null;
  /** Adds the previewed items to that case instead of creating a new one. */
  onAddTo?: (overlap: InvestigationOverlap) => void;
  adding?: boolean;
  addError?: string;
}

/**
 * Active investigations that already cover part of the draft population. Sorted by how many
 * items they own so the one that matters most is first.
 */
export const ActiveOverlapsAlert: React.FC<ActiveOverlapsAlertProps> = ({
  stage,
  overlaps,
  totalItems,
  unassignedCount,
  currentUser,
  onAddTo,
  adding,
  addError,
}) => {
  const shown = withoutCancelled(overlaps);
  if (shown.length === 0) return null;
  const sorted = [...shown].sort(
    (a, b) => (b.overlappingCount ?? 0) - (a.overlappingCount ?? 0)
  );
  const n = sorted.length;
  const mostly = mostlyCoveredBy(sorted, totalItems);
  const mostlyTitle = mostly?.title || mostly?.investigationId || "";
  const canAdd = !!(
    mostly &&
    onAddTo &&
    currentUser &&
    mostly.owner === currentUser
  );
  const openButton = mostly ? (
    <Button
      href={investigationPath(mostly.investigationId || "", stage)}
      target="_blank"
      iconName="external"
      iconAlign="right"
      ariaLabel={`Open ${mostlyTitle} in a new tab`}
    >
      Open {mostlyTitle}
    </Button>
  ) : undefined;
  return (
    <Alert
      type="warning"
      header={
        mostly
          ? `Mostly covered by “${mostlyTitle}” (${sharedItemsLabel(
              mostly.overlappingCount,
              totalItems
            )})`
          : `${n} active investigation${n === 1 ? "" : "s"} already cover${
              n === 1 ? "s" : ""
            } part of this population`
      }
      action={
        canAdd && mostly ? (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              loading={adding}
              onClick={() => onAddTo?.(mostly)}
            >
              Add to {mostlyTitle}
            </Button>
            {openButton}
          </SpaceBetween>
        ) : (
          openButton
        )
      }
    >
      <SpaceBetween size="xs">
        {mostly && (
          <Box variant="p">
            {canAdd
              ? "It's yours: add these items to it instead of creating a near-duplicate. New matching items go to it automatically."
              : "Work on that investigation instead of creating a near-duplicate: new matching items go to it automatically."}
          </Box>
        )}
        {addError && (
          <Box variant="p" color="text-status-error">
            {addError}
          </Box>
        )}
        <Box variant="p">{overlapChoicesSentence(sorted, unassignedCount)}</Box>
        <CollapsibleRows
          noun="investigation"
          rows={sorted.map((o) => (
            <OverlapRow
              key={o.investigationId}
              stage={stage}
              overlap={o}
              details={[
                (o.status && STATUS_LABELS[o.status]) || o.status || "",
                o.owner ? `owner ${o.owner}` : "needs an owner",
                sharedItemsLabel(o.overlappingCount, totalItems),
                relationLabel(o.relation),
              ]}
            />
          ))}
        />
      </SpaceBetween>
    </Alert>
  );
};

interface HistoricalOverlapsAlertProps {
  stage: Stage;
  overlaps: InvestigationOverlap[];
}

interface DuplicateRuleAlertProps {
  stage: Stage;
  /** `current` from the DUPLICATE_RULE conflict: the active case that already has this rule. */
  existing: Investigation;
  /** Optional in-app navigation (e.g. the dashboard drawer); the link always works as a plain href. */
  onOpen?: (investigationId: string) => void;
  /** RULE_OVERLAP rather than DUPLICATE_RULE: the rules overlap without either containing the other. */
  overlap?: boolean;
}

/**
 * Shown when creating was refused because an active investigation already has exactly this rule
 * (or one that overlaps it without containment). Not a warning about the population — nothing
 * was created — but a redirect: the work is there.
 */
export const DuplicateRuleAlert: React.FC<DuplicateRuleAlertProps> = ({
  stage,
  existing,
  onOpen,
  overlap,
}) => (
  <Alert
    type="info"
    header={
      overlap
        ? "This rule overlaps an active investigation"
        : "This investigation already exists"
    }
    data-testid="duplicate-rule-alert"
    action={
      <Button
        onClick={() => onOpen?.(existing.id)}
        href={onOpen ? undefined : investigationPath(existing.id, stage)}
        target={onOpen ? undefined : "_blank"}
        iconName={onOpen ? undefined : "external"}
        iconAlign="right"
      >
        Open investigation
      </Button>
    }
  >
    <SpaceBetween size="xxs">
      <Box variant="p">
        {overlap
          ? "Its rule and yours overlap without either being narrower, so neither could own the shared items. Nothing was created: add a field, remove the overlap, or contribute to it."
          : "An active investigation with exactly this rule is already open, so nothing new was created. Contribute to it instead."}
      </Box>
      <OverlapRow
        stage={stage}
        overlap={{ investigationId: existing.id, title: existing.title }}
        details={[
          (existing.status && STATUS_LABELS[existing.status]) ||
            existing.status ||
            "",
          existing.owner ? `owner ${existing.owner}` : "needs an owner",
          typeof existing.assignedCount === "number"
            ? `${existing.assignedCount.toLocaleString()} items`
            : "",
        ]}
      />
    </SpaceBetween>
  </Alert>
);

/** Resolved investigations whose rule overlapped: prior art, with the conclusion up front. */
export const HistoricalOverlapsAlert: React.FC<
  HistoricalOverlapsAlertProps
> = ({ stage, overlaps }) => {
  const resolved = withoutCancelled(overlaps);
  if (resolved.length === 0) return null;
  const n = resolved.length;
  return (
    <Alert
      type="info"
      header={`${n} resolved investigation${
        n === 1 ? "" : "s"
      } covered similar items`}
    >
      <CollapsibleRows
        noun="investigation"
        rows={resolved.map((o) => (
          <OverlapRow
            key={o.investigationId}
            stage={stage}
            overlap={o}
            details={[
              o.resolutionReason
                ? resolutionReasonLabel(o.resolutionReason)
                : "Resolved",
              o.owner ? `by ${o.owner}` : "",
              o.conclusion ? `“${o.conclusion}”` : "no conclusion recorded",
            ]}
          />
        ))}
      />
    </Alert>
  );
};
